<template>
  <div :class="['LocationCircle', circleClasses]" @click="handleClick">
    <div
      v-if="showBoard && hasBoards"
      :class="['floating-board', { 'board-gray': !isPlayable }]"
      @click.stop="handleBoardClick"
    >
      <GamIcon :name="GamIconName.GAMBIT" size="14px" />
    </div>

    <div class="circle-ring">
      <div class="circle-main" :class="{ skeleton: loading }">
        <img
          v-if="location.coverBlobHash"
          :src="imageSrc"
          :alt="location.name"
          loading="lazy"
          @load="onImageLoad"
          @error="onImageError"
        />
        <GamIcon v-else :name="typeIcon" size="28px" />
      </div>

      <div v-if="showIrl && hasCheckin" class="badge badge-tr">
        <GamIcon :name="GamIconName.PAWN" size="14px" />
      </div>

      <div v-if="showIrl && hasGambit" class="badge badge-br">
        <GamIcon :name="GamIconName.PLAY_EMPTY" size="14px" />
      </div>
    </div>

    <span class="circle-name">{{ location.name || '' }}</span>

    <span v-if="temporalLabel && showIrl" :class="['circle-temporal', `temporal-${temporalLabel}`]">
      {{ localize(`app.component.map.pin.${temporalLabel}`) }}
    </span>
  </div>
</template>

<script setup lang="ts">
import type { BaseLocationDto } from '@/core/data/location/location.interface';
import { gambits, localize } from '@/core/gambits';
import GambitHelper from '@/core/helpers/gambit.helper';
import LocationHelper from '@/core/helpers/location.helper';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamDateType } from '@/views/composables/constants/components/gamGambit.constants';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { computed, ref } from 'vue';

const props = withDefaults(
  defineProps<{
    location: BaseLocationDto;
    selected?: boolean;
    /** Shows the image disc as a skeleton, e.g. while a photo is uploading. */
    loading?: boolean;
    mode?: 'all' | 'boards' | 'irl' | 'none';
  }>(),
  { mode: 'all' },
);

const showBoard = computed(() => props.mode === 'all' || props.mode === 'boards');
const showIrl = computed(() => props.mode === 'all' || props.mode === 'irl');

const emit = defineEmits<{
  click: [location: BaseLocationDto];
  clickBoard: [location: BaseLocationDto];
}>();

const imageLoaded = ref(false);

const onImageLoad = () => {
  imageLoaded.value = true;
};
const onImageError = () => {
  imageLoaded.value = false;
};

const imageSrc = computed(() => {
  if (!props.location.coverBlobHash) return '';
  return gambits.imageService.getImageSrc(props.location.coverBlobHash, 160);
});

const typeIcon = computed(() => {
  return LocationHelper.getLocationTypeIcon(props.location.type, props.location.private, props.location.favorite);
});

const hasCheckin = computed(() => {
  return (props.location.visitors?.length ?? 0) > 0;
});

const hasGambit = computed(() => {
  return (props.location.nextGambits?.length ?? 0) > 0;
});

const hasBoards = computed(() => {
  return (props.location.activeChessboardCount ?? 0) > 0;
});

const isPlayable = computed(() => {
  return props.location.hasPlayableBoard !== false;
});

const soonestGambitDateType = computed((): GamDateType | null => {
  const nextGambits = props.location.nextGambits;
  if (!nextGambits?.length) return null;

  const sorted = [...nextGambits].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

  for (const g of sorted) {
    const endDate = 'end' in g && typeof g.end === 'string' ? g.end : null;
    const dateType = GambitHelper.getGambitsDateType(g.start, false, endDate);
    if (dateType === GamDateType.NOW) return GamDateType.NOW;
    if (dateType === GamDateType.SOON) return GamDateType.SOON;
    if (dateType === GamDateType.TODAY) return GamDateType.TODAY;
    if (dateType === GamDateType.TOMORROW) return GamDateType.TOMORROW;
  }

  return null;
});

const isLiveNow = computed(() => {
  const dt = soonestGambitDateType.value;
  return hasCheckin.value || dt === GamDateType.NOW || dt === GamDateType.SOON;
});

const hasActivity = computed(() => {
  return hasCheckin.value || hasGambit.value || hasBoards.value;
});

const temporalLabel = computed((): string | null => {
  const dt = soonestGambitDateType.value;
  if (!dt) return null;
  if (dt === GamDateType.NOW) return 'live';
  if (dt === GamDateType.SOON) return 'soon';
  if (dt === GamDateType.TODAY) return 'today';
  if (dt === GamDateType.TOMORROW) return 'tomorrow';
  return null;
});

const circleClasses = computed(() => ({
  selected: props.selected,
  'is-loading': props.loading,
  'has-activity': hasActivity.value,
  'has-irl': hasGambit.value,
  'has-checkin': hasCheckin.value,
  'has-boards': hasBoards.value,
  'is-live': isLiveNow.value,
  'has-cover': !!props.location.coverBlobHash,
  'image-loaded': imageLoaded.value,
}));

const handleClick = () => {
  emit('click', props.location);
};

const handleBoardClick = () => {
  emit('clickBoard', props.location);
};
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
.LocationCircle {
  --circle-size: 80px;
  --ring-offset: 6px;
  --ring-width: 2px;
  --badge-size: 32px;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-s);
  width: calc(var(--circle-size) + 2 * var(--ring-offset));
  cursor: pointer;
  position: relative;
  text-decoration: none;
  transition: opacity var(--transition);

  @include hover {
    opacity: 0.85;
  }

  // The skeleton disc lets taps through to this handler, so stop them here.
  &.is-loading {
    pointer-events: none;
  }
}

.circle-ring {
  width: calc(var(--circle-size) + 2 * var(--ring-offset));
  height: calc(var(--circle-size) + 2 * var(--ring-offset));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: var(--ring-offset);
  box-sizing: border-box;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    padding: var(--ring-width);
    background: transparent;
    mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
    transition: background 200ms ease;
  }
}

.has-irl .circle-ring::before,
.LocationCircle.selected .circle-ring::before {
  background: var(--color-linear-gradient);
}

.has-boards:not(.has-irl) .circle-ring::before {
  background: rgba(255, 255, 255, 0.7);
}

.has-boards:not(.has-irl) .circle-main {
  outline: none;
}

.circle-main {
  width: var(--circle-size);
  height: var(--circle-size);
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-dark-500);
  position: relative;
  transition: box-shadow var(--transition);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    opacity: 0;
    transition: opacity var(--transition-router);
  }
}

.image-loaded .circle-main img {
  opacity: 1;
}

.LocationCircle:not(.has-irl):not(.has-checkin) .circle-main img {
  filter: grayscale(100%);
}

.LocationCircle:not(.has-irl) .circle-main {
  outline: 1px solid var(--color-white-30);
}

.is-live .circle-main {
  box-shadow:
    0 0 20px 6px var(--color-pink-30),
    0 0 44px 10px var(--color-pink-20);
}

.badge {
  position: absolute;
  width: var(--badge-size);
  height: var(--badge-size);
  border-radius: 50%;
  background: var(--color-linear-gradient);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  border: 2px solid var(--color-dark-700);
}

.badge-tr {
  top: -2px;
  right: -2px;
}

.badge-br {
  bottom: -2px;
  right: -2px;
}

.circle-name {
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: var(--color-white-100);
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  max-width: 100%;
  line-height: 1.3;
  word-break: break-word;
}

.circle-temporal {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--color-pink);
  line-height: 1;
  margin-top: -2px;
}

.floating-board {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  width: 26px;
  height: 26px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
  cursor: pointer;

  :deep(svg) {
    fill: white;
  }

  &.board-gray {
    opacity: 0.4;
  }
}
</style>
